import { useCallback, useState } from 'react';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { KanbanBoard } from '@/components/kanban/kanban-board';
import { TaskDialog } from '@/components/tasks/task-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkActionBar } from '@/components/ui/bulk-action-bar';
import { useTasks, useDeleteTask, useBulkDeleteTasks, useBulkUpdateTasks } from '@/hooks/use-tasks';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useDebounce } from '@/hooks/use-debounce';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  CheckSquare,
  Search,
  Calendar,
  Clock,
  ArrowUpDown,
  Trash2,
  Repeat,
} from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { cn, formatDate } from '@/lib/utils';

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-500/10 text-red-400 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  low: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

const statusLabels: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
};

export function TasksPage() {
  useDocumentTitle('Tasks');

  const [search, setSearch] = useState('');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const { user } = useAuthStore();

  const deleteTask = useDeleteTask();
  const bulkDeleteTasks = useBulkDeleteTasks();
  const bulkUpdateTasks = useBulkUpdateTasks();

  const { data, isLoading, isError, refetch } = useTasks();
  const tasks = data?.data || [];
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const debouncedSearch = useDebounce(search, 300);

  const filteredTasks = debouncedSearch
    ? tasks.filter((t: any) =>
        t.title?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        t.assignee?.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : tasks;

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredTasks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTasks.map((t: any) => t.id)));
    }
  }, [filteredTasks, selectedIds.size]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await bulkDeleteTasks.mutateAsync(Array.from(selectedIds));
      toast({
        title: 'Tasks deleted',
        description: `${selectedIds.size} task(s) have been deleted.`,
        variant: 'success',
      });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({
        title: 'Failed to delete tasks',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleBulkStatusChange = async (status: string) => {
    if (selectedIds.size === 0) return;
    try {
      await bulkUpdateTasks.mutateAsync({
        ids: Array.from(selectedIds),
        updates: { status },
      });
      toast({
        title: 'Tasks updated',
        description: `${selectedIds.size} task(s) moved to ${status.replace('_', ' ')}.`,
        variant: 'success',
      });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({
        title: 'Failed to update tasks',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleBulkPriorityChange = async (priority: string) => {
    if (selectedIds.size === 0) return;
    try {
      await bulkUpdateTasks.mutateAsync({
        ids: Array.from(selectedIds),
        updates: { priority },
      });
      toast({
        title: 'Tasks updated',
        description: `${selectedIds.size} task(s) set to ${priority} priority.`,
        variant: 'success',
      });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({
        title: 'Failed to update tasks',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const [deleteConfirmTask, setDeleteConfirmTask] = useState<any>(null);

  const handleDeleteTask = async (task: any) => {
    setDeleteConfirmTask(task);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmTask) return;

    try {
      await deleteTask.mutateAsync(deleteConfirmTask.id);
      toast({
        title: 'Task deleted',
        description: `"${deleteConfirmTask.title}" has been deleted.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to delete task',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setDeleteConfirmTask(null);
    }
  };

  // Tasks grouped by status for list view
  const tasksByStatus = {
    todo: filteredTasks.filter((t: any) => t.status === 'todo'),
    in_progress: filteredTasks.filter((t: any) => t.status === 'in_progress'),
    review: filteredTasks.filter((t: any) => t.status === 'review'),
    done: filteredTasks.filter((t: any) => t.status === 'done'),
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Tasks</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Manage and track all tasks across your projects.
          </p>
        </div>
        {user?.role !== 'client' && (
          <button
            onClick={() => {
              setSelectedTask(null);
              setTaskDialogOpen(true);
            }}
            className="btn-dyndev text-sm"
          >
            + New Task
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
        <Input
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          aria-label="Search tasks"
        />
      </div>

      {isError ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-red-400 mb-4">Failed to load tasks.</p>
          <button onClick={() => refetch()} className="btn-dyndev text-sm">
            Try Again
          </button>
        </div>
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-[var(--border-color)] p-4 space-y-3">
              <Skeleton className="h-5 w-3/5" />
              <Skeleton className="h-3 w-full" />
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="h-64 flex items-center justify-center">
          <EmptyState
            icon={CheckSquare}
            title="No tasks yet"
            description="Tasks will appear here once they're created in projects."
          />
        </div>
      ) : (
        <Tabs defaultValue="kanban" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
          </TabsList>

          <TabsContent value="kanban">
            <KanbanBoard
              tasks={filteredTasks}
              isLoading={false}
              onTaskClick={(task) => {
                setSelectedTask(task);
                setTaskDialogOpen(true);
              }}
              onTaskCreate={() => {
                setSelectedTask(null);
                setTaskDialogOpen(true);
              }}
            />
          </TabsContent>

          <TabsContent value="list">
            {/* Bulk select bar for list view */}
            <div className="flex items-center justify-between mb-4 px-1">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all-tasks"
                  checked={selectedIds.size === filteredTasks.length && filteredTasks.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <label htmlFor="select-all-tasks" className="text-xs text-[var(--text-secondary)] cursor-pointer select-none">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
                </label>
              </div>
            </div>
            <div className="space-y-4">
              {Object.entries(tasksByStatus).map(([status, statusTasks]: [string, any]) => (
                <div key={status}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={cn(
                      'w-2 h-2 rounded-full',
                      status === 'todo' ? 'bg-gray-400' :
                      status === 'in_progress' ? 'bg-blue-400' :
                      status === 'review' ? 'bg-yellow-400' :
                      'bg-dyndev-green'
                    )} />
                    <h3 className="text-sm font-semibold">{statusLabels[status]}</h3>
                    <span className="text-xs text-[var(--text-secondary)]">({statusTasks.length})</span>
                  </div>

                  {statusTasks.length > 0 ? (
                    <div className="space-y-2 mb-6">
                      {statusTasks.map((task: any) => (
                        <Card
                          key={task.id}
                          className="cursor-pointer hover:border-dyndev-green/30 transition-colors"
                        >
                          <CardContent className="p-4" onClick={() => {
                            setSelectedTask(task);
                            setTaskDialogOpen(true);
                          }}>
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={selectedIds.has(task.id)}
                                onCheckedChange={() => toggleSelect(task.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 mt-0.5"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{task.title}</p>
                                {task.description && (
                                  <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-1">
                                    {task.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-3 mt-2">
                                  {task.assignee && (
                                    <div className="flex items-center gap-1">
                                      <Avatar className="w-4 h-4">
                                        <AvatarFallback className="text-[6px] bg-dyndev-green/20 text-dyndev-green">
                                          {task.assignee.name?.charAt(0)?.toUpperCase() || '?'}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span className="text-[10px] text-[var(--text-secondary)]">{task.assignee.name}</span>
                                    </div>
                                  )}
                                  {task.dueDate && (
                                    <span className="flex items-center gap-1 text-[10px] text-[var(--text-secondary)]">
                                      <Calendar size={10} />
                                      {formatDate(task.dueDate)}
                                    </span>
                                  )}
                                  {task.estimatedHours && (
                                    <span className="flex items-center gap-1 text-[10px] text-[var(--text-secondary)]">
                                      <Clock size={10} />
                                      {task.estimatedHours}h
                                    </span>
                                  )}
                                  {task.isRecurring && (
                                    <span className="flex items-center gap-0.5 text-[10px] text-dyndev-green" title={task.recurringRule?.freq ? `Every ${task.recurringRule.interval || 1} ${task.recurringRule.freq}(s)` : 'Recurring'}>
                                      <Repeat size={10} />
                                      Recurring
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                {task.priority && (
                                  <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', priorityColors[task.priority])}>
                                    {task.priority}
                                  </Badge>
                                )}
                                {task.project && (
                                  <span className="text-[10px] text-[var(--text-secondary)]">{task.project.name}</span>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTask(task);
                                  }}
                                  className="text-[var(--text-secondary)] hover:text-red-400 transition-colors"
                                  title="Delete task"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[var(--text-secondary)] mb-6 pl-1">No tasks in this status.</p>
                  )}
                </div>
              ))}
            </div>

            {/* Bulk action bar for list view */}
            <BulkActionBar
              selectedCount={selectedIds.size}
              onClearSelection={() => setSelectedIds(new Set())}
              onDelete={handleBulkDelete}
              onStatusChange={handleBulkStatusChange}
              statusOptions={[
                { value: 'todo', label: 'Todo' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'review', label: 'Review' },
                { value: 'done', label: 'Done' },
              ]}
              onPriorityChange={handleBulkPriorityChange}
              priorityOptions={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
              isDeleting={bulkDeleteTasks.isPending}
              isUpdatingStatus={bulkUpdateTasks.isPending}
              isUpdatingPriority={bulkUpdateTasks.isPending}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* Task dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) setSelectedTask(null);
        }}
        task={selectedTask}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirmTask} onOpenChange={(open) => { if (!open) setDeleteConfirmTask(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirmTask?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
